import type { FeedbackStatus, FeedbackType } from "@anota/shared";

export type Lang = "es" | "pt" | "en";

const es = {
  /** Para fechas y "hace 5 min". */
  lang: "es" as Lang,
  launch: "Anotar",
  launchTitle: "Dejar una nota sobre esta página",
  reviewBar: "Estás revisando esta página: señala lo que quieras cambiar.",
  hideBar: "Ocultar la barra",
  pickHint: "Haz clic en lo que quieres cambiar",
  pickHintTouch: "Toca lo que quieres cambiar",
  pickKeys: "Con el teclado: Tab hasta lo que quieres cambiar y Enter para elegirlo",
  shortcutMac: "⌘ + Enter para enviar",
  tagNames: {
    a: "el enlace", button: "el botón", img: "la imagen", picture: "la imagen", svg: "el icono", video: "el vídeo",
    h1: "el título", h2: "el título", h3: "el título", h4: "el título", h5: "el título", h6: "el título", p: "el texto", span: "el texto",
    li: "el elemento de la lista", ul: "la lista", ol: "la lista", nav: "el menú", header: "la cabecera", footer: "el pie de página",
    form: "el formulario", input: "el campo", textarea: "el campo", select: "el desplegable", label: "la etiqueta", table: "la tabla",
    section: "la sección", article: "el artículo",
  } as Record<string, string>,
  tagOther: "este elemento",
  pickHintDrag: "Haz clic en lo que quieres cambiar, o arrastra para marcar un área",
  areaIn: "Sobre un área de",
  areaPage: "Sobre un área de la página",
  drawOn: "Marcar en la captura",
  drawAgain: "Volver a marcar",
  drawMarked: "Captura con tus marcas",
  drawTitle: "Marca lo que quieras en la captura",
  tools: { box: "Recuadro", arrow: "Flecha", pen: "Lápiz", blur: "Difuminar" },
  undo: "Deshacer",
  drawDone: "Listo",
  wholePage: "Toda la página",
  cancel: "Cancelar",
  newNote: "Nueva nota",
  about: "Sobre",
  wholePageNote: "Nota sobre toda la página",
  pointIn: "Sobre un punto de",
  pointPage: "Sobre un punto de la página",
  placeholder: "¿Qué quieres cambiar? Cuanto más concreto, mejor.",
  types: { change: "Cambio", bug: "Error", content: "Texto", question: "Pregunta", idea: "Idea" } as Record<FeedbackType, string>,
  typeLabel: "Tipo",
  name: "Tu nombre",
  email: "Tu email",
  optional: "opcional",
  screenshot: "Adjuntar captura de pantalla",
  send: "Enviar",
  sending: "Enviando…",
  capturing: "Haciendo la captura…",
  sent: (n?: number) => (n ? `¡Gracias! Nota #${n} enviada.` : "¡Gracias! Nota enviada."),
  error: "No se pudo enviar. Inténtalo de nuevo.",
  required: "Escribe la nota antes de enviarla.",
  invalidEmail: "Ese email no parece válido.",
  close: "Cerrar",
  shortcut: "Ctrl + Enter para enviar",
  notesHere: (n: number) => (n === 1 ? "1 nota aquí" : `${n} notas aquí`),
  notesTitle: "Notas en esta página",
  hidePins: "Ocultar pines",
  showPins: "Mostrar pines",
  notFound: "No se encuentra en esta página",
  wholePageShort: "Toda la página",
  reply: "Responder",
  replyPlaceholder: "Escribe una respuesta…",
  noteN: (n: number) => `Nota #${n}`,
  done: "Qué se hizo:",
  team: "Equipo",
  agent: "Agente",
  mine: "Tus notas",
  seeMine: "Ver tus notas",
  mineEmpty: "Aquí verás las notas que envíes desde este navegador, con su estado y las respuestas del equipo.",
  mineError: "No se pudieron cargar tus notas. Inténtalo de nuevo.",
  loading: "Cargando…",
  onPage: "En",
  thisPage: "esta página",
  replies: (n: number) => (n === 1 ? "1 respuesta" : `${n} respuestas`),
  news: (n: number) => (n === 1 ? "1 novedad en tus notas" : `${n} novedades en tus notas`),
  dismissNews: "Ocultar el aviso",
  isNew: "Nuevo",
  you: "Tú",
  showWhere: "Ver dónde está",
  mineEmptyTitle: "Aún no hay notas",
  notesCount: (n: number) => (n === 1 ? "1 nota" : `${n} notas`),
  filterAll: "Todas",
  filterActive: "Pendientes",
  filterClosed: "Cerradas",
  retry: "Reintentar",
  verdictAsk: "¿Está bien así?",
  verdictOk: "Sí, está bien",
  verdictNo: "No, sigue mal",
  verdictOkBody: "Está bien así, gracias.",
  verdictWhy: "¿Qué sigue mal?",
  verdictSend: "Reabrir la nota",
  confirmed: "Confirmaste que está bien.",
  otherDevice: "¿Quieres verlas en otro dispositivo?",
  copyLink: "Copiar el enlace a tus notas",
  linkCopied: "Enlace copiado. Ábrelo en el móvil o guárdalo: quien lo tenga verá tus notas.",
  linkManual: "Copia este enlace (quien lo tenga verá tus notas):",
  stepSent: "Enviada",
  statusHint: {
    open: "Recibida: el equipo la revisará pronto.",
    in_progress: "El equipo está trabajando en ella.",
    resolved: "Hecha. Dinos si está bien así o qué sigue mal.",
    wont_fix: "El equipo decidió no hacer este cambio.",
    duplicate: "Ya había otra nota igual; se sigue en esa.",
  } as Record<FeedbackStatus, string>,
  status: { open: "Abierta", in_progress: "En curso", resolved: "Resuelta", wont_fix: "No se hará", duplicate: "Duplicada" } as Record<FeedbackStatus, string>,
};

type Strings = typeof es;

const pt: Strings = {
  lang: "pt",
  launch: "Anotar",
  launchTitle: "Deixar uma nota sobre esta página",
  reviewBar: "Você está revisando esta página: aponte o que quer mudar.",
  hideBar: "Ocultar a barra",
  pickHint: "Clique no que você quer mudar",
  pickHintTouch: "Toque no que você quer mudar",
  pickKeys: "Com o teclado: Tab até o que quer mudar e Enter para escolher",
  shortcutMac: "⌘ + Enter para enviar",
  tagNames: {
    a: "o link", button: "o botão", img: "a imagem", picture: "a imagem", svg: "o ícone", video: "o vídeo",
    h1: "o título", h2: "o título", h3: "o título", h4: "o título", h5: "o título", h6: "o título", p: "o texto", span: "o texto",
    li: "o item da lista", ul: "a lista", ol: "a lista", nav: "o menu", header: "o cabeçalho", footer: "o rodapé",
    form: "o formulário", input: "o campo", textarea: "o campo", select: "a lista suspensa", label: "o rótulo", table: "a tabela",
    section: "a seção", article: "o artigo",
  },
  tagOther: "este elemento",
  pickHintDrag: "Clique no que você quer mudar, ou arraste para marcar uma área",
  areaIn: "Sobre uma área de",
  areaPage: "Sobre uma área da página",
  drawOn: "Marcar na captura",
  drawAgain: "Marcar de novo",
  drawMarked: "Captura com suas marcas",
  drawTitle: "Marque o que quiser na captura",
  tools: { box: "Retângulo", arrow: "Seta", pen: "Lápis", blur: "Desfocar" },
  undo: "Desfazer",
  drawDone: "Pronto",
  wholePage: "Página inteira",
  cancel: "Cancelar",
  newNote: "Nova nota",
  about: "Sobre",
  wholePageNote: "Nota sobre a página inteira",
  pointIn: "Sobre um ponto de",
  pointPage: "Sobre um ponto da página",
  placeholder: "O que você quer mudar? Quanto mais concreto, melhor.",
  types: { change: "Mudança", bug: "Erro", content: "Texto", question: "Pergunta", idea: "Ideia" },
  typeLabel: "Tipo",
  name: "Seu nome",
  email: "Seu e-mail",
  optional: "opcional",
  screenshot: "Anexar captura de tela",
  send: "Enviar",
  sending: "Enviando…",
  capturing: "Capturando a tela…",
  sent: (n?: number) => (n ? `Obrigado! Nota #${n} enviada.` : "Obrigado! Nota enviada."),
  error: "Não foi possível enviar. Tente de novo.",
  required: "Escreva a nota antes de enviar.",
  invalidEmail: "Esse e-mail não parece válido.",
  close: "Fechar",
  shortcut: "Ctrl + Enter para enviar",
  notesHere: (n: number) => (n === 1 ? "1 nota aqui" : `${n} notas aqui`),
  notesTitle: "Notas nesta página",
  hidePins: "Ocultar marcadores",
  showPins: "Mostrar marcadores",
  notFound: "Não encontrada nesta página",
  wholePageShort: "Página inteira",
  reply: "Responder",
  replyPlaceholder: "Escreva uma resposta…",
  noteN: (n: number) => `Nota #${n}`,
  done: "O que foi feito:",
  team: "Equipe",
  agent: "Agente",
  mine: "Suas notas",
  seeMine: "Ver suas notas",
  mineEmpty: "Aqui você verá as notas que enviar deste navegador, com o status e as respostas da equipe.",
  mineError: "Não foi possível carregar suas notas. Tente de novo.",
  loading: "Carregando…",
  onPage: "Em",
  thisPage: "esta página",
  replies: (n: number) => (n === 1 ? "1 resposta" : `${n} respostas`),
  news: (n: number) => (n === 1 ? "1 novidade nas suas notas" : `${n} novidades nas suas notas`),
  dismissNews: "Ocultar o aviso",
  isNew: "Novo",
  you: "Você",
  showWhere: "Ver onde está",
  mineEmptyTitle: "Ainda não há notas",
  notesCount: (n: number) => (n === 1 ? "1 nota" : `${n} notas`),
  filterAll: "Todas",
  filterActive: "Pendentes",
  filterClosed: "Fechadas",
  retry: "Tentar de novo",
  verdictAsk: "Ficou bom assim?",
  verdictOk: "Sim, está bom",
  verdictNo: "Não, ainda está errado",
  verdictOkBody: "Ficou bom, obrigado.",
  verdictWhy: "O que ainda está errado?",
  verdictSend: "Reabrir a nota",
  confirmed: "Você confirmou que está bom.",
  otherDevice: "Quer vê-las em outro dispositivo?",
  copyLink: "Copiar o link das suas notas",
  linkCopied: "Link copiado. Abra no celular ou guarde: quem tiver o link verá suas notas.",
  linkManual: "Copie este link (quem o tiver verá suas notas):",
  stepSent: "Enviada",
  statusHint: {
    open: "Recebida: a equipe vai revisá-la em breve.",
    in_progress: "A equipe está trabalhando nela.",
    resolved: "Feita. Diga se ficou bom ou o que ainda está errado.",
    wont_fix: "A equipe decidiu não fazer esta mudança.",
    duplicate: "Já havia outra nota igual; segue naquela.",
  },
  status: { open: "Aberta", in_progress: "Em andamento", resolved: "Resolvida", wont_fix: "Não será feita", duplicate: "Duplicada" },
};

const en: Strings = {
  lang: "en",
  launch: "Feedback",
  launchTitle: "Leave a note about this page",
  reviewBar: "You are reviewing this page: point at anything you want changed.",
  hideBar: "Hide the bar",
  pickHint: "Click what you want to change",
  pickHintTouch: "Tap what you want to change",
  pickKeys: "With the keyboard: Tab to what you want to change and press Enter",
  shortcutMac: "⌘ + Enter to send",
  tagNames: {
    a: "the link", button: "the button", img: "the image", picture: "the image", svg: "the icon", video: "the video",
    h1: "the heading", h2: "the heading", h3: "the heading", h4: "the heading", h5: "the heading", h6: "the heading", p: "the text", span: "the text",
    li: "the list item", ul: "the list", ol: "the list", nav: "the menu", header: "the header", footer: "the footer",
    form: "the form", input: "the field", textarea: "the field", select: "the dropdown", label: "the label", table: "the table",
    section: "the section", article: "the article",
  },
  tagOther: "this element",
  pickHintDrag: "Click what you want to change, or drag to mark an area",
  areaIn: "About an area in",
  areaPage: "About an area of the page",
  drawOn: "Mark up the screenshot",
  drawAgain: "Mark up again",
  drawMarked: "Screenshot with your marks",
  drawTitle: "Mark anything on the screenshot",
  tools: { box: "Box", arrow: "Arrow", pen: "Pen", blur: "Blur" },
  undo: "Undo",
  drawDone: "Done",
  wholePage: "Whole page",
  cancel: "Cancel",
  newNote: "New note",
  about: "About",
  wholePageNote: "Note about the whole page",
  pointIn: "About a spot in",
  pointPage: "About a spot on the page",
  placeholder: "What would you like to change? The more specific, the better.",
  types: { change: "Change", bug: "Bug", content: "Copy", question: "Question", idea: "Idea" },
  typeLabel: "Type",
  name: "Your name",
  email: "Your email",
  optional: "optional",
  screenshot: "Attach a screenshot",
  send: "Send",
  sending: "Sending…",
  capturing: "Taking the screenshot…",
  sent: (n?: number) => (n ? `Thanks! Note #${n} sent.` : "Thanks! Note sent."),
  error: "Could not send. Please try again.",
  required: "Write the note before sending it.",
  invalidEmail: "That email doesn't look valid.",
  close: "Close",
  shortcut: "Ctrl + Enter to send",
  notesHere: (n: number) => (n === 1 ? "1 note here" : `${n} notes here`),
  notesTitle: "Notes on this page",
  hidePins: "Hide pins",
  showPins: "Show pins",
  notFound: "Not found on this page",
  wholePageShort: "Whole page",
  reply: "Reply",
  replyPlaceholder: "Write a reply…",
  noteN: (n: number) => `Note #${n}`,
  done: "What was done:",
  team: "Team",
  agent: "Agent",
  mine: "Your notes",
  seeMine: "See your notes",
  mineEmpty: "Notes you send from this browser will show up here, with their status and the team's replies.",
  mineError: "Couldn't load your notes. Please try again.",
  loading: "Loading…",
  onPage: "On",
  thisPage: "this page",
  replies: (n: number) => (n === 1 ? "1 reply" : `${n} replies`),
  news: (n: number) => (n === 1 ? "1 update on your notes" : `${n} updates on your notes`),
  dismissNews: "Dismiss",
  isNew: "New",
  you: "You",
  showWhere: "Show me where",
  mineEmptyTitle: "No notes yet",
  notesCount: (n: number) => (n === 1 ? "1 note" : `${n} notes`),
  filterAll: "All",
  filterActive: "Pending",
  filterClosed: "Closed",
  retry: "Try again",
  verdictAsk: "Is it right now?",
  verdictOk: "Yes, it's right",
  verdictNo: "No, still wrong",
  verdictOkBody: "Looks good, thanks.",
  verdictWhy: "What's still wrong?",
  verdictSend: "Reopen the note",
  confirmed: "You confirmed it's right.",
  otherDevice: "Want to see them on another device?",
  copyLink: "Copy the link to your notes",
  linkCopied: "Link copied. Open it on your phone or save it: anyone with it can see your notes.",
  linkManual: "Copy this link (anyone with it can see your notes):",
  stepSent: "Sent",
  statusHint: {
    open: "Received: the team will review it soon.",
    in_progress: "The team is working on it.",
    resolved: "Done. Let us know if it's right or what's still wrong.",
    wont_fix: "The team decided not to make this change.",
    duplicate: "There was already a note like this; it continues there.",
  },
  status: { open: "Open", in_progress: "In progress", resolved: "Resolved", wont_fix: "Won't do", duplicate: "Duplicate" },
};

const ALL: Record<Lang, Strings> = { es, pt, en };

/** Idioma: el del proyecto o el del `<script>`; si no, el de la página (`<html lang>`) y luego el del navegador; español por defecto. */
export function pickLang(...candidates: (string | null | undefined)[]): Lang {
  const page = typeof document !== "undefined" ? document.documentElement.lang : null;
  for (const c of [...candidates, page, ...(typeof navigator !== "undefined" ? navigator.languages : [])]) {
    const code = c?.slice(0, 2).toLowerCase();
    if (code === "es" || code === "pt" || code === "en") return code;
  }
  return "es";
}

export const strings = (lang: Lang): Strings => ALL[lang];
export type { Strings };
